// Pre-flight selection modal for explicit downgrades. Renders when the
// /v1/billing/subscription/preview response shows the tenant is over the
// target plan's agent cap. Customer picks which N stay active; the rest
// are soft-disabled when the change-plan POST fires.
//
// Two surfaces of information:
//   • Agent checkbox list — the actionable part. Each row carries name,
//     model, last-modified relative time. preselectedActive defaults the
//     top-N (by updated_at desc) checked.
//   • KB summary — read-only ("X of Y documents will be excluded"). KB
//     selection is system-pick only in v1; the dashboard's KB page lets
//     the customer re-include individual docs post-fact.
//
// Confirm button is disabled until exactly N agents are selected. Off-by-
// one prevention is critical: the backend rejects a too-short selection
// with selection_required and a too-long one with the same code, but
// keeping it tight in the UI saves a round-trip.

import { useEffect, useState } from 'react';
import { Archive, ArrowRight, Bot, FileText } from 'lucide-react';
import type { DowngradePreview } from '@cortex/shared';
import { Button } from '../ui/button.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog.js';

interface Props {
  open: boolean;
  preview: DowngradePreview | null;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (keepActiveAgentIds: string[]) => void;
}

export function DowngradeSelectionModal({
  open,
  preview,
  pending,
  error,
  onCancel,
  onConfirm,
}: Props) {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Reset when the modal opens with a new preview — preselect the top-N
  // (preselectedActive) so the default state matches what reconcileTenantToPlan
  // would do server-side without a selection.
  useEffect(() => {
    if (!preview) return;
    const next = new Set<string>();
    for (const a of preview.agents.list) {
      if (a.preselectedActive) next.add(a.id);
    }
    setSelected(next);
  }, [preview]);

  if (!preview) return null;

  const cap = preview.agents.cap ?? preview.agents.activeCount;
  const required = cap; // Need EXACTLY cap selected (under-cap downgrade wouldn't open this modal)
  const canConfirm = selected.size === required && !pending;

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onCancel();
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Switching to {preview.targetPlan} — choose what stays active</DialogTitle>
          <DialogDescription>
            The {preview.targetPlan} plan includes {String(cap)} {cap === 1 ? 'agent' : 'agents'},
            and you currently have {String(preview.agents.activeCount)}. Pick the {String(required)}{' '}
            you want to keep active. The rest will be disabled — your data is preserved and you can
            re-enable on upgrade or remove permanently.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <section className="space-y-2">
            <header className="flex items-center justify-between text-sm">
              <span className="font-medium">Agents</span>
              <span className="text-muted-foreground">
                {String(selected.size)} / {String(required)} selected
              </span>
            </header>
            <ul className="max-h-72 space-y-1 overflow-y-auto rounded-md border p-1">
              {preview.agents.list.map((a) => {
                const checked = selected.has(a.id);
                const overSoft = !checked && selected.size >= required;
                return (
                  <li key={a.id}>
                    <label
                      className={`flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm hover:bg-muted ${
                        checked ? 'bg-muted/40' : ''
                      } ${overSoft ? 'opacity-50' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={overSoft}
                        onChange={() => { toggle(a.id); }}
                        className="h-4 w-4 accent-primary"
                      />
                      <Bot className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                      <span className="min-w-0 flex-1">
                        <span className="block font-medium">{a.name}</span>
                        <span className="block text-xs text-muted-foreground">
                          {a.model} · updated {new Date(a.updatedAt).toLocaleDateString()}
                        </span>
                      </span>
                      {a.preselectedActive && (
                        <span className="text-xs text-muted-foreground">recently active</span>
                      )}
                    </label>
                  </li>
                );
              })}
            </ul>
          </section>

          {preview.kbDocuments.overage.count > 0 && (
            <section className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
              <div className="flex items-start gap-2">
                <FileText className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                <div className="min-w-0">
                  <p className="font-medium">
                    {String(preview.kbDocuments.overage.count)} KB{' '}
                    {preview.kbDocuments.overage.count === 1 ? 'document' : 'documents'} will be
                    excluded from retrieval
                  </p>
                  <p className="text-xs text-muted-foreground">
                    The {String(preview.kbDocuments.countCap ?? 0)} most recently modified stay
                    active. Excluded docs remain in storage; re-include them individually from the
                    KB page later if you upgrade.
                  </p>
                </div>
              </div>
            </section>
          )}

          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant="default"
            disabled={!canConfirm}
            onClick={() => { onConfirm([...selected]); }}
          >
            {pending ? (
              'Switching…'
            ) : (
              <>
                Confirm switch <ArrowRight aria-hidden />
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Exported so the upgrade page can also render the same Archive icon
// in its inline preview banner. Keeps icon usage centralised.
export { Archive };
